// src/components/Login.js

import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { auth, db } from '../firebase';
import {
  sendEmailVerification,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      const plan = searchParams.get('plan');

      // Existing users remain compatible. New users do not get an application
      // profile or access until Firebase reports that their email is verified.
      if (!userDoc.exists() && !user.emailVerified) {
        try {
          await sendEmailVerification(user);
        } catch (verificationError) {
          console.warn('Verification email could not be resent:', verificationError);
        }
        await signOut(auth);
        toast.info('Verify your email before signing in. We sent a new verification email.');
        return;
      }

      if (!userDoc.exists()) {
        await setDoc(userRef, {
          email: user.email,
          videoCount: 0,
          role: 'user',
          subscriptionTier: plan === 'free' ? 'free' : 'none',
          credits: 0,
          createdAt: serverTimestamp(),
          emailVerifiedAt: serverTimestamp(),
        });
      }

      const userRole = userDoc.exists() ? userDoc.data().role : 'user';

      if (userRole === 'admin') {
        navigate('/admin/home');
      } else if (!userDoc.exists() && plan && plan !== 'free') {
        navigate(`/purchase-direct?plan=${encodeURIComponent(plan)}&newSignup=true`);
      } else {
        navigate('/dashboard/home');
      }

      toast.success('Logged in successfully!');
    } catch (error) {
      console.error('Error logging in:', error);
      toast.error('Unable to sign in with those credentials.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <form
        onSubmit={handleLogin}
        className="bg-white p-6 rounded shadow-md w-full max-w-sm"
      >
        <h2 className="text-2xl font-semibold mb-4">Login</h2>

        {searchParams.get('verifyEmail') === '1' && (
          <p className="mb-4 rounded bg-blue-50 p-3 text-sm text-blue-800">
            Check your inbox and verify your email before signing in.
          </p>
        )}

        <div className="mb-4">
          <label htmlFor="email" className="block text-gray-700 mb-2">
            Email:
          </label>
          <input
            type="email"
            id="email"
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            maxLength={254}
            required
          />
        </div>

        <div className="mb-4">
          <label htmlFor="password" className="block text-gray-700 mb-2">
            Password:
          </label>
          <input
            type="password"
            id="password"
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-blue-600 text-white p-2 rounded hover:bg-blue-700 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? 'Signing in…' : 'Login'}
        </button>

        <p className="mt-4 text-center">
          Don't have an account?{' '}
          <Link to="/signup?plan=free" className="text-blue-500 hover:underline">
            Sign up for free
          </Link>
        </p>
      </form>
    </div>
  );
}

export default Login;
